import { useMemo } from 'react';
import styled from 'styled-components';
import { GapProps, SizeProps } from '@/types/ui';
import { getSizeStyle } from '@/utils/style/ui';

const DEFAULT_GAP = 71;
const DEFAULT_ANIMATION_DURATION = 100;
const DEFAULT_REPEAT = 8;

interface StyleProps {
  gap?: GapProps;
  width?: SizeProps;
  height?: SizeProps;
  animationDuration?: number;
}
export interface Props extends StyleProps {
  isReverse?: boolean;
  repeat?: number;
  items?: React.ReactNode[];
}

const Rail = styled.div<{ isReverse: Props['isReverse'] }>`
  display: flex;
  align-items: center;
  animation: ${({ isReverse }) => (isReverse ? 'slide-reverse' : 'slide')} 100s
    linear infinite forwards;
  /* will-change: transform; */
  ${({ theme }) => theme.transition.default}

  @keyframes slide {
    100% {
      transform: translateX(-50%);
    }
  }
  @keyframes slide-reverse {
    100% {
      transform: translateX(50%);
    }
  }
`;
const Root = styled.div<StyleProps>`
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  width: ${({ theme, width }) =>
    getSizeStyle({ theme, size: width }) || 'unset'};
  max-width: 100%;
  height: ${({ theme, height }) =>
    getSizeStyle({ theme, size: height }) || 'unset'};
  overflow: hidden;

  ${Rail} {
    ${({ theme, gap }) =>
      getSizeStyle({ theme, size: gap, key: 'gap' }) ||
      `gap: ${DEFAULT_GAP}px;`}
    ${({ animationDuration = DEFAULT_ANIMATION_DURATION }) =>
      animationDuration ? `animation-duration: ${animationDuration}s` : ''}
  }
`;

export default function SliderBelt({
  isReverse,
  repeat = DEFAULT_REPEAT,
  items: itemsProps,
  ...props
}: Props) {
  // memo
  const items = useMemo(
    () => [...Array(repeat).keys()].map(() => itemsProps ?? []).flat(),
    [itemsProps, repeat]
  );

  return (
    <Root {...props}>
      <Rail isReverse={isReverse}>{items?.map((item) => item)}</Rail>
    </Root>
  );
}
